import { IChoiceGroupOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  ChoiceGroup,
  choiceGroupStylesHorizontalBox,
  DatePicker,
  MessageBarList,
  messageBarListStylesToast,
  navigate,
  useClassNames,
  useMessageBarList,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { FeatureStoreService } from '../../featurestore/featurestoreApi';
import {
  ActivePermission,
  CoreService,
  ListableFeatureSet,
  RetrieveRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  OnlineRetrieveResponse,
  OnlineRetrieveTokenResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/OnlineApi.pb';
import { buttonStylesPrimaryPanel, ContentsTitle, DatetimeFormatType, NameValue } from '../../featurestore/models';
import { formatDate, getErrorMessageBarMessage, hasGreaterPermission } from '../../featurestore/utils';
import { EditNameValueList } from '../EditNameValueList/EditNameValueList';
import { NameValueViewer } from '../NameValueViewer/NameValueViewer';
import { IRetrieveClassNames, IRetrieveStyles, retrieveStylesDefault } from './Retrieve.styles';

const convertJsonStringToNameValueList = (jsonString = '') => {
  const nameValues: NameValue[] = [];
  if (jsonString) {
    const json = JSON.parse(jsonString);
    if (json) {
      Object.keys(json).forEach((name) => {
        if (name) {
          nameValues.push({ name, value: json[name] });
        }
      });
    }
  }
  return nameValues;
};

const downloadJson = (name: string, jsonString: string) => {
  const blob = new Blob([jsonString], { type: 'text/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  navigate(url, true, `${name?.replace(' ', '_')}.json`);
};

enum Labels {
  online = 'Retrieve Online',
  offline = 'Retrieve Offline',
}

const options: IChoiceGroupOption[] = [
  { key: Labels.offline, text: Labels.offline },
  { key: Labels.online, text: Labels.online },
];

interface IRetrieveProps {
  featureSet?: ListableFeatureSet;
  onSave?: (isOnline?: boolean) => void;
  onCancel: () => void;
}

export const Retrieve = ({
  featureSet: {
    name: featureSetName,
    id: featureSetId,
    version: featureSetVersion,
    permission,
    primaryKey = [],
    timeTravelColumnFormat,
    recordsCount = '0',
  } = {},
  onSave,
  onCancel,
}: IRetrieveProps) => {
  const [optionLabel, setOptionLabel] = useState(
    hasGreaterPermission(permission, ActivePermission.ACTIVE_PERMISSION_EDITOR) && +recordsCount > 0
      ? Labels.offline
      : Labels.online
  );
  const [nameValues, setNameValues] = useState<NameValue[]>(primaryKey.map((key) => ({ name: key, value: '' })));
  const [values, setValues] = useState<RetrieveRequest>({ featureSetId, featureSetVersion });
  const [result, setResult] = useState<string>();
  const { messages, addMessage, removeMessage } = useMessageBarList();
  const { addToast } = useToast();
  const onChange = useCallback((_e?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    if (option) {
      setOptionLabel(option.key as Labels);
    }
  }, []);
  const onUpdate = useCallback(async (values: RetrieveRequest) => {
    try {
      await fsApi(CoreService.StartRetrieveAsLinksJob, values);
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Start Retrieve Job Successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while starting retrieve job: `));
    } finally {
      onSave && onSave();
    }
  }, []);
  const onUpdateOnline = useCallback(async () => {
    try {
      const { token, signature }: OnlineRetrieveTokenResponse = await fsApi(CoreService.GenerateOnlineRetrievalToken, {
        featureSetId,
        featureSetVersion,
      });
      const { row }: OnlineRetrieveResponse = await fsApi(FeatureStoreService.OnlineRetrieve, {
        token,
        signature,
        keys: nameValues,
        featureSetId,
        featureSetMajorVersion: `${parseInt(featureSetVersion || '0')}`,
      });
      setResult(row);
    } catch (err) {
      const { messageBarType, message } = getErrorMessageBarMessage(
        err,
        `An error has occurred while starting online retrieval: `
      );
      addMessage(message as string, undefined, undefined, messageBarType);
    }
  }, [nameValues]);
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      if (optionLabel === Labels.offline) {
        onUpdate(values);
      } else {
        if (result) {
          downloadJson(featureSetName || 'result', result);
          onSave && onSave(true);
        } else {
          onUpdateOnline();
        }
      }
      e.preventDefault();
    },
    [values, optionLabel, result]
  );
  const classNames = useClassNames<IRetrieveStyles, IRetrieveClassNames>('Retrieve', retrieveStylesDefault);
  return (
    <div className={classNames.root} data-test="retrieve">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.messageBar}>
          <MessageBarList
            styles={messageBarListStylesToast}
            messages={messages}
            autoClose
            groupByMessage
            onRemoveMessage={removeMessage}
          />
        </div>
        {!result ? (
          <div className={classNames.fields}>
            {hasGreaterPermission(permission, ActivePermission.ACTIVE_PERMISSION_EDITOR) && +recordsCount > 0 ? (
              <ChoiceGroup
                styles={choiceGroupStylesHorizontalBox}
                options={options}
                required
                label="Select Retrieve Source"
                key="schema-type"
                defaultSelectedKey={Labels.offline}
                onChange={onChange}
              />
            ) : (
              <div className={classNames.subTitle}>{Labels.online}</div>
            )}
            {optionLabel === Labels.offline ? (
              <>
                <div>Time Travel Column Format: {timeTravelColumnFormat || 'no format'}</div>
                <DatePicker
                  label="Scope Start Date"
                  disabled={!timeTravelColumnFormat}
                  onSelectDate={(startDateTime) => {
                    if (startDateTime) {
                      setValues({
                        ...values,
                        startDateTime: formatDate(startDateTime, timeTravelColumnFormat, DatetimeFormatType.spark),
                      });
                    }
                  }}
                />
                <DatePicker
                  label="Scope End Date"
                  disabled={!timeTravelColumnFormat}
                  onSelectDate={(endDateTime) => {
                    if (endDateTime) {
                      setValues({
                        ...values,
                        endDateTime: formatDate(endDateTime, timeTravelColumnFormat, DatetimeFormatType.spark),
                      });
                    }
                  }}
                />
              </>
            ) : (
              <EditNameValueList
                editable
                nameValues={nameValues}
                onUpdate={(nameValues) => {
                  setNameValues(nameValues);
                }}
              />
            )}
          </div>
        ) : (
          <div className={classNames.result}>
            <NameValueViewer
              data={convertJsonStringToNameValueList(result)}
              onError={(err, msg) => {
                const { messageBarType, message } = getErrorMessageBarMessage(err, msg);
                addMessage(message as string, undefined, undefined, messageBarType);
              }}
            />
          </div>
        )}
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} />
          <Button
            styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
            type="submit"
            text={result ? ContentsTitle.download : ContentsTitle.retrieve}
          />
        </div>
      </form>
    </div>
  );
};
